import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Click-to-copy email address.
 * On click: copies to clipboard, shows toast + inline check icon for 1.5s.
 * No mailto: — avoids launching the OS default mail app.
 *
 * Usage:
 *   <CopyableEmail email="hnel@1on1trade.com" className="text-amber-500" />
 */
export default function CopyableEmail({
  email,
  className = '',
  iconSize = 14,
  showIcon = true,
  testId,
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(email);
    } catch {
      // Fallback for older browsers / non-secure contexts
      const ta = document.createElement('textarea');
      ta.value = email;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success('Email copied to clipboard', {
      description: email,
      duration: 2500,
    });
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={`inline-flex items-center gap-1.5 cursor-pointer transition-colors ${className}`}
      title="Click to copy"
      aria-label={`Copy email address ${email} to clipboard`}
      data-testid={testId || `copy-email-${email}`}
    >
      <span>{email}</span>
      {showIcon && (copied
        ? <Check size={iconSize} className="text-emerald-400 shrink-0" />
        : <Copy size={iconSize} className="opacity-50 group-hover:opacity-100 shrink-0" />
      )}
    </button>
  );
}
